import { RotateCcw, Printer } from 'lucide-react'
import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { salesService, type SaleDetail } from '../../../services/sales.service'
import { useQuery } from '@tanstack/react-query'
import { DiscreteLoader } from '../../ui/discrete-loader'
import { TicketPreviewModal } from '../../pos/modals/TicketPreviewModal'
import { useSessionStore } from '../../../store/auth/auth.store'
import { saleItemsForTicket } from '../../../lib/sale-ticket'

interface SaleDetailModalProps {
  isOpen: boolean
  onClose: () => void
  sale: { id: string | number } | null
  onOpenReturnModal: (sale: any, type: 'partial' | 'total') => void
  onOpenCancelModal: (sale: any) => void
  autoPrint?: boolean
  onPrintComplete?: () => void
  headless?: boolean
  initialTicketCopies?: number
}

function deliveryDetailsForTicket(saleDetail: SaleDetail) {
  const delivery = saleDetail.delivery
  const deliveryPerson = delivery?.delivery_user
    ? [delivery.delivery_user.name, delivery.delivery_user.last_name].filter(Boolean).join(' ')
    : ''

  return {
    deliveryUserId: delivery?.delivery_user?.id ?? null,
    deliveryPerson,
    recipientName: delivery?.recipient_name ?? saleDetail.customer?.name ?? '',
    customerPhone: delivery?.recipient_phone ?? saleDetail.customer?.phone ?? '',
    customerAddress: delivery?.address ?? '',
    observations: delivery?.observations ?? '',
    deliveryCost: Number(delivery?.delivery_fee_amount ?? saleDetail.amounts?.delivery_fee ?? 0),
  }
}

function saleTypeForTicket(saleDetail: SaleDetail): 'delivery' | 'regular' {
  return saleDetail.is_delivery || saleDetail.delivery ? 'delivery' : 'regular'
}

export function SaleDetailModal({ isOpen, onClose, sale, onOpenReturnModal, onOpenCancelModal, autoPrint = false, onPrintComplete, headless = false, initialTicketCopies = 1 }: SaleDetailModalProps) {
  const canPrintTicket = useSessionStore((state) => state.canAction('sales.ticket.print'))
  const canCreateSaleReturn = useSessionStore((state) => state.canAction('sales.sale-return.create'))
  const canCancelSale = useSessionStore((state) => state.canAction('sales.sale.cancel'))
  const allowPrint = headless || canPrintTicket
  const [ticketCopies, setTicketCopies] = useState(initialTicketCopies)
  const [showPrintPreview, setShowPrintPreview] = useState(false)

  const { data: saleDetail, isLoading } = useQuery({
    queryKey: ['saleDetail', sale?.id],
    queryFn: () => salesService.getSale(sale!.id),
    enabled: !!sale?.id && isOpen,
  })

  useEffect(() => {
    if (isOpen && saleDetail && autoPrint && allowPrint && !isLoading) {
      setShowPrintPreview(true)
      onPrintComplete?.()
    }
  }, [allowPrint, autoPrint, isLoading, isOpen, onPrintComplete, saleDetail])

  useEffect(() => {
    if (isOpen) {
      setTicketCopies(initialTicketCopies)
    }
  }, [isOpen, initialTicketCopies])

  if (!isOpen || !sale) return null

  if (headless) {
    return (
      <>
        {showPrintPreview && saleDetail && (
          <TicketPreviewModal
            open={showPrintPreview}
            onOpenChange={setShowPrintPreview}
            isPrintOnly={true}
            ticketNumber={saleDetail.ticket_number}
            branchName={saleDetail.sucursal?.name || '—'}
            cashRegisterName={saleDetail.cash_register?.name || '—'}
            cashierName={`${saleDetail.seller?.name || ''} ${saleDetail.seller?.last_name || ''}`.trim() || 'Admin'}
            customerType={saleDetail.customer?.id ? 'registered' : 'general'}
            selectedCustomer={saleDetail.customer?.id ?? null}
            customers={saleDetail.customer?.id ? [{ id: saleDetail.customer.id, name: saleDetail.customer.name }] : []}
            saleType={saleTypeForTicket(saleDetail)}
            deliveryDetails={deliveryDetailsForTicket(saleDetail)}
            cart={saleItemsForTicket(saleDetail)}
            subtotal={Number(saleDetail.amounts?.subtotal || 0)}
            discountTotal={Number(saleDetail.amounts?.discount_total || 0)}
            savingsTotal={Number(saleDetail.amounts?.savings_total || 0)}
            tax={Number(saleDetail.amounts?.tax_total || 0)}
            deliveryCost={Number(saleDetail.amounts?.delivery_fee || 0)}
            pointsDiscount={0}
            total={Number(saleDetail.amounts?.total || 0)}
            pointsEarned={0}
            selectedPaymentMethod={(saleDetail as any).payments?.[0]?.paymentMethod?.code || saleDetail.payment?.method?.name || 'cash'}
            selectedBank={(saleDetail as any).payments?.[0]?.bank_name || ''}
            reference={(saleDetail as any).payments?.[0]?.reference || ''}
            cashReceived={String((saleDetail as any).payments?.[0]?.cash_received_amount || saleDetail.amounts?.total)}
            change={Number((saleDetail as any).payments?.[0]?.change_amount || 0)}
            requiresInvoice={(saleDetail as any).requires_invoice}
            ticketCopies={ticketCopies}
            completeSale={() => {
              setShowPrintPreview(false)
              onClose()
              return true
            }}
            setShowPaymentModal={() => setShowPrintPreview(false)}
          />
        )}
      </>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="border-b border-slate-200 px-5 py-4 pr-14">
          <div className="flex flex-wrap items-center justify-between gap-3 w-full">
            <div className="flex flex-wrap items-center gap-3">
              <DialogTitle>Detalle de Venta</DialogTitle>
            </div>
            {saleDetail && !isLoading && canPrintTicket && (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={ticketCopies || ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value)
                    setTicketCopies(isNaN(val) ? 0 : val)
                  }}
                  onBlur={() => {
                    if (!ticketCopies || ticketCopies < 1) setTicketCopies(1)
                  }}
                  className="h-9 w-16 text-center"
                  title="Copias a imprimir"
                />
                <Button variant="default" size="sm" onClick={() => setShowPrintPreview(true)}>
                  <Printer className="mr-2 h-4 w-4" />
                  Imprimir
                </Button>
              </div>
            )}
          </div>
          <DialogDescription className="sr-only">
            Información completa de la venta y productos vendidos
          </DialogDescription>
        </DialogHeader>
        
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 flex justify-center">
               <DiscreteLoader text="Cargando detalles de la venta..." />
            </div>
          ) : saleDetail ? (
          <div className="space-y-6">
            {/* Sale Info */}
            <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-4">
              <div>
                <p className="text-sm text-slate-600">Ticket:</p>
                <p className="font-semibold text-slate-900">{saleDetail.ticket_number}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Fecha y Hora:</p>
                <p className="font-semibold text-slate-900">
                  {new Date(saleDetail.completed_at.endsWith('Z') ? saleDetail.completed_at : saleDetail.completed_at.replace(' ', 'T') + 'Z').toLocaleString('es-MX', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Cliente:</p>
                <p className="font-semibold text-slate-900">{saleDetail.customer?.name || 'Mostrador'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Vendedor:</p>
                <p className="font-semibold text-slate-900">
                  {[saleDetail.seller?.name, saleDetail.seller?.last_name].filter(Boolean).join(' ') || 'No disponible'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Usuario que cobró:</p>
                <p className="font-semibold text-slate-900">
                  {[saleDetail.performed_by?.name, saleDetail.performed_by?.last_name].filter(Boolean).join(' ') || 'No disponible'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Sucursal:</p>
                <p className="font-semibold text-slate-900">{saleDetail.sucursal?.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Método de Pago:</p>
                <p className="font-semibold text-slate-900">{saleDetail.payment?.method?.name || 'N/A'}</p>
              </div>
            </div>

            {/* Items */}
            <div>
              <h4 className="mb-3 font-medium text-card-foreground ">Productos</h4>
              <div className="overflow-hidden rounded-lg border border-slate-200">
                <table className="w-full border-collapse">
                  <thead className="border-b border-border bg-table-header-bg">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground">Producto</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground">Cantidad</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground">Precio</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground">Ahorro</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground">Subtotal</th>
                      {saleDetail.status !== 'COMPLETED' && (
                        <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground">Devuelto</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {saleDetail.items.map((item) => (
                      <tr key={item.id} >
                        <td className="px-4 py-2">
                          <p>{item.product_name}</p>
                          {item.discount_snapshot?.name && <p className="mt-0.5 text-xs font-medium text-purple-600">{item.discount_snapshot.name}</p>}
                        </td>
                        <td className="px-4 py-2">{item.quantity}</td>
                        <td className="px-4 py-2">${Number(item.unit_price).toFixed(2)}</td>
                        <td className="px-4 py-2">${Number(item.savings_amount ?? item.discount_amount).toFixed(2)}</td>
                        <td className="px-4 py-2 font-medium">${Number(item.subtotal).toFixed(2)}</td>
                        {saleDetail.status !== 'COMPLETED' && (
                          <td className="px-4 py-2">
                            {Number(item.returned_quantity) > 0 && (
                              <Badge className="bg-red-100 text-red-700">{item.returned_quantity}</Badge>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="space-y-2 border-t border-slate-200 pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-card-description">Subtotal:</span>
                <span className="font-medium">${Number(saleDetail.amounts?.subtotal || 0).toFixed(2)}</span>
              </div>
              {Number(saleDetail.amounts?.discount_total || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-card-description">Descuento antes de IVA:</span>
                  <span className="font-medium">-${Number(saleDetail.amounts.discount_total).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-card-description">IVA (16%):</span>
                <span className="font-medium">${Number(saleDetail.amounts?.tax_total || 0).toFixed(2)}</span>
              </div>
              {Number(saleDetail.amounts?.savings_total || 0) > 0 && (
                <div className="flex justify-between text-sm text-purple-700">
                  <span>Ahorro promocional (IVA incl.):</span>
                  <span className="font-semibold">-${Number(saleDetail.amounts.savings_total).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-slate-200 pt-2">
                <span className="font-semibold text-card-description">Total:</span>
                <span className="text-xl font-semibold text-card-description">${Number(saleDetail.amounts?.total || 0).toFixed(2)}</span>
              </div>
              {saleDetail.returns && saleDetail.returns.length > 0 && (
                <div className="flex justify-between border-t border-slate-200 pt-2">
                  <span className="font-medium text-orange-600">Total Reembolsado:</span>
                  <span className="text-lg font-medium text-orange-600">
                    -${saleDetail.returns.reduce((acc: number, ret: any) => acc + Number(ret.amounts?.total_refund || 0), 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            {/* Return Info */}
            {saleDetail.returns && saleDetail.returns.length > 0 && (
              <div className="rounded-lg border border-border bg-card p-4">
                <p className="mb-3 text-sm font-semibold text-card-foreground">Información de Devoluciones</p>
                {saleDetail.returns.map((ret, idx) => (
                  <div key={ret.id || idx} className="border-l-2 border-primary pl-4">
                    <p className="text-sm text-card-description">Fecha: {ret.processed_at ? new Date(ret.processed_at.endsWith('Z') ? ret.processed_at : ret.processed_at.replace(' ', 'T') + 'Z').toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short'}) : 'Pendiente'}</p>
                    <p className="text-sm text-card-description">Motivo: {ret.reason}</p>
                    <p className="text-sm text-card-description">Monto Reembolsado: ${Number(ret.amounts?.total_refund || 0).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {canCancelSale && saleDetail.actions?.can_cancel && (
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    onClose()
                    onOpenCancelModal({
                      id: saleDetail.id,
                      paymentMethod: saleDetail.payment?.method?.name || '',
                      ticketNumber: saleDetail.ticket_number,
                      invoiced: (saleDetail as any).invoiced,
                      cfdi_id: (saleDetail as any).cfdi_id ?? null,
                      cfdi_cancelled_at: (saleDetail as any).cfdi_cancelled_at ?? null,
                    })
                  }}
                >
                  Cancelar Venta Completa
                </Button>
              )}
              {canCreateSaleReturn && saleDetail.actions?.can_return && (
                <>
                  <Button
                    onClick={() => {
                      onClose()
                      onOpenReturnModal(saleDetail, 'partial')
                    }}
                    variant="outline"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Devolución Parcial
                  </Button>
                  <Button
                    onClick={() => {
                      onClose()
                      onOpenReturnModal(saleDetail, 'total')
                    }}
                    variant="outline"
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Devolución Total
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
           <div className="py-10 text-center text-slate-500">
             Ocurrió un error al cargar la venta.
           </div>
        )}
        </div>
      </DialogContent>

      {showPrintPreview && saleDetail && (
        <TicketPreviewModal
          open={showPrintPreview}
          onOpenChange={setShowPrintPreview}
          isPrintOnly={true}
          ticketNumber={saleDetail.ticket_number}
          branchName={saleDetail.sucursal?.name || '—'}
          cashRegisterName={saleDetail.cash_register?.name || '—'}
          cashierName={`${saleDetail.seller?.name || ''} ${saleDetail.seller?.last_name || ''}`.trim() || 'Admin'}
          customerType={saleDetail.customer?.id ? 'registered' : 'general'}
          selectedCustomer={saleDetail.customer?.id ?? null}
          customers={saleDetail.customer?.id ? [{ id: saleDetail.customer.id, name: saleDetail.customer.name }] : []}
          saleType={saleTypeForTicket(saleDetail)}
          deliveryDetails={deliveryDetailsForTicket(saleDetail)}
          cart={saleItemsForTicket(saleDetail)}
          subtotal={Number(saleDetail.amounts?.subtotal || 0)}
          discountTotal={Number(saleDetail.amounts?.discount_total || 0)}
          savingsTotal={Number(saleDetail.amounts?.savings_total || 0)}
          tax={Number(saleDetail.amounts?.tax_total || 0)}
          deliveryCost={Number(saleDetail.amounts?.delivery_fee || 0)}
          pointsDiscount={Number(saleDetail.loyalty?.credit_applied || 0)}
          grossTotal={Number(saleDetail.amounts?.total || 0)}
          total={Number(saleDetail.loyalty?.external_amount ?? saleDetail.amounts?.total ?? 0)}
          pointsEarned={Number(saleDetail.loyalty?.points_earned || 0)}
          pointsRedeemed={Number(saleDetail.loyalty?.points_redeemed || 0)}
          creditGenerated={Number(saleDetail.loyalty?.credit_generated || 0)}
          creditRemaining={Number(saleDetail.loyalty?.credit_balance_after || 0)}
          selectedPaymentMethod={(saleDetail as any).payments?.[0]?.paymentMethod?.code || saleDetail.payment?.method?.name || 'cash'}
          selectedBank={(saleDetail as any).payments?.[0]?.bank_name || ''}
          reference={(saleDetail as any).payments?.[0]?.reference || ''}
          cashReceived={String((saleDetail as any).payments?.[0]?.cash_received_amount || saleDetail.amounts?.total)}
          change={Number((saleDetail as any).payments?.[0]?.change_amount || 0)}
          requiresInvoice={(saleDetail as any).requires_invoice}
          ticketCopies={ticketCopies}
          completeSale={() => { setShowPrintPreview(false); return true }}
          setShowPaymentModal={() => setShowPrintPreview(false)}
        />
      )}
    </Dialog>
  )
}
